import React, { useState, useEffect } from 'react';
import documentService from '../../services/documentService';

const PatientDocuments = ({ patient }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file_type: 'Other',
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState('');
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentUrl, setDocumentUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Debug: Log patient prop
  useEffect(() => {
    console.log('PatientDocuments received patient:', patient);
  }, [patient]);

  useEffect(() => {
    if (patient?.id) {
      console.log('Fetching documents for patient ID:', patient.id);
      fetchDocuments();
    } else {
      console.warn('No patient or patient.id found:', patient);
      setLoading(false);
    }
  }, [patient]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentService.getPatientDocuments(patient.id);
      // Backend returns data.data.documents
      setDocuments(response.data?.documents || []);
      setError('');
    } catch (error) {
      console.error('Error fetching documents:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMsg = 'Failed to load documents. Please try again.';
      if (error.response?.status === 401) {
        errorMsg = 'Unauthorized. Please log in again.';
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      }
      
      setError(errorMsg);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      console.error('Invalid file type:', file.type);
      setError(`Invalid file type: ${file.type}. Only PNG, JPG, and PDF are allowed.`);
      setSelectedFile(null);
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      console.error('File too large:', file.size);
      setError('File size exceeds 5MB limit.');
      setSelectedFile(null);
      return;
    }

    console.log('✅ File validation passed');
    setSelectedFile(file);
    setError('');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    if (!patient?.id) {
      setError('Patient ID is missing');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('patient_id', patient.id.toString());
      formData.append('file_type', uploadForm.file_type);

      console.log('Uploading document:', {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileType: uploadForm.file_type,
        patientId: patient.id
      });

      const response = await documentService.uploadDocument(formData);
      console.log('Upload response:', response);
      
      // Reset form
      setSelectedFile(null);
      setUploadForm({ file_type: 'Other' });
      const fileInput = document.getElementById('file-input');
      if (fileInput) fileInput.value = '';
      
      // Refresh documents
      await fetchDocuments();
      
      // Show success message briefly
      setError('');
    } catch (err) {
      console.error('Upload error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      let errorMsg = 'Failed to upload document';
      
      if (err.response?.status === 401) {
        errorMsg = 'Unauthorized. Please log in again.';
      } else if (err.response?.status === 404) {
        errorMsg = 'Patient not found.';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.message) {
        errorMsg = err.message;
      }
      
      setError(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (document) => {
    if (!window.confirm(`Delete ${document.original_filename}?`)) {
      return;
    }

    try {
      await documentService.deleteDocument(document.id);
      await fetchDocuments();
    } catch (error) {
      console.error('Delete error:', error);
      const errorMsg = error.response?.data?.error || 'Failed to delete document';
      alert(errorMsg);
    }
  };

  const handleDownload = (document) => {
    const url = documentService.getDocumentUrl(document.id);
    window.open(url, '_blank');
  };

  const handleView = async (document) => {
    setViewingDocument(document);
    setLoadingPreview(true);
    setDocumentUrl(null);

    try {
      // Fetch the document as a blob with authentication
      const blob = await documentService.downloadDocumentBlob(document.id);
      const url = URL.createObjectURL(blob);
      setDocumentUrl(url);
    } catch (error) {
      console.error('Failed to load document:', error);
      alert('Failed to load document preview');
      setViewingDocument(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleCloseViewer = () => {
    if (documentUrl) {
      URL.revokeObjectURL(documentUrl); // Clean up blob URL
    }
    setViewingDocument(null);
    setDocumentUrl(null);
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('image')) return '🖼️';
    return '📎';
  };

  const getTypeColor = (type) => {
    const colors = {
      'X-ray': 'bg-blue-100 text-blue-800',
      'MRI': 'bg-purple-100 text-purple-800',
      'Report': 'bg-green-100 text-green-800',
      'Photo': 'bg-yellow-100 text-yellow-800',
      'Other': 'bg-gray-100 text-gray-800',
    };
    return colors[type] || colors['Other'];
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // If patient is not provided
  if (!patient) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-700 text-sm">Please select a patient to view documents.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Upload New Document
        </h3>
        
        {error && (
          <div className="mb-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleUpload} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Document Type
              </label>
              <select
                value={uploadForm.file_type}
                onChange={(e) => setUploadForm({ ...uploadForm, file_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={uploading}
              >
                <option value="X-ray">X-ray</option>
                <option value="MRI">MRI Scan</option>
                <option value="Report">Medical Report</option>
                <option value="Photo">Progress Photo</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select File (PNG, JPG, PDF - Max 5MB)
              </label>
              <input
                id="file-input"
                type="file"
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={uploading}
              />
            </div>
          </div>

          {selectedFile && (
            <div className="bg-white p-3 rounded border border-gray-300">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-xl">{getFileIcon(selectedFile.type)}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null);
                    const fileInput = document.getElementById('file-input');
                    if (fileInput) fileInput.value = '';
                  }}
                  className="text-red-600 hover:text-red-700 text-sm"
                  disabled={uploading}
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={uploading || !selectedFile}
            className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          >
            {uploading ? 'Uploading...' : '📤 Upload Document'}
          </button>
        </form>
      </div>

      {/* Documents List */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Documents ({documents.length})
        </h3>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : error && documents.length === 0 ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
            <button
              onClick={fetchDocuments}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
            >
              Try Again
            </button>
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500">No documents uploaded yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="bg-white p-3 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <span className="text-2xl">{getFileIcon(doc.mime_type)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {doc.original_filename}
                      </p>
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <span className={`px-2 py-0.5 rounded ${getTypeColor(doc.file_type)}`}>
                          {doc.file_type}
                        </span>
                        <span>{formatFileSize(doc.file_size)}</span>
                        <span>•</span>
                        <span>{formatDate(doc.uploaded_at)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-3">
                    <button
                      onClick={() => handleView(doc)}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      title="View"
                    >
                      👁️
                    </button>
                    <button
                      onClick={() => handleDownload(doc)}
                      className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700"
                      title="Download"
                    >
                      ⬇️
                    </button>
                    <button
                      onClick={() => handleDelete(doc)}
                      className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{getFileIcon(viewingDocument.mime_type)}</span>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {viewingDocument.original_filename}
                  </h3>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span className={`px-2 py-0.5 rounded ${getTypeColor(viewingDocument.file_type)}`}>
                      {viewingDocument.file_type}
                    </span>
                    <span>{formatFileSize(viewingDocument.file_size)}</span>
                    <span>•</span>
                    <span>{formatDate(viewingDocument.uploaded_at)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleCloseViewer}
                className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
              >
                ×
              </button>
            </div>

            {/* Document Content */}
            <div className="flex-1 overflow-auto p-6 bg-gray-50">
              {loadingPreview ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : viewingDocument.mime_type?.includes('image') ? (
                <div className="flex items-center justify-center h-full">
                  <img
                    src={documentUrl}
                    alt={viewingDocument.original_filename}
                    className="max-w-full max-h-full object-contain rounded shadow-lg"
                  />
                </div>
              ) : viewingDocument.mime_type?.includes('pdf') ? (
                <iframe
                  src={documentUrl}
                  className="w-full h-full min-h-[600px] rounded shadow-lg"
                  title={viewingDocument.original_filename}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <span className="text-6xl mb-4">📄</span>
                  <p className="text-lg mb-2">Preview not available</p>
                  <p className="text-sm mb-4">This file type cannot be previewed in the browser</p>
                  <button
                    onClick={() => handleDownload(viewingDocument)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    Download to View
                  </button>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => handleDownload(viewingDocument)}
                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
              >
                ⬇️ Download
              </button>
              <button
                onClick={handleCloseViewer}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientDocuments;